/**
 * The twenty-one permission codes `internal/rvm` declares (design C12 §6.1,
 * `server/internal/rvm/service.go:29-57`).
 *
 * Three of these are halves of pairs the screens have to know about, not just
 * the server:
 *
 *  - **`forecast.manage` ⊗ `forecast.approve`** — the person who builds the
 *    number a property is run to must not be the person who signs it off. A
 *    revenue manager therefore sees no Approve control at all, rather than a
 *    disabled one: a disabled button teaches an operator that the system is
 *    broken rather than that the control is deliberate.
 *  - **`displacement.evaluate` ⊗ `displacement.decide`** — the person who
 *    computes what a group is worth must not be the person who accepts it.
 *  - **`variance.explain` is separate from `forecast.publish`.** Publishing is
 *    refused while a material movement is unexplained (§9.5), and the two acts
 *    are deliberately different permissions so the explanation is written by
 *    somebody who knows the market rather than by whoever happens to hold the
 *    publish key.
 *
 * `snapshot.take` is its own code and does NOT imply `snapshot.read` being
 * enough to take one: a snapshot is immutable and is never retaken (§4.4), so
 * taking one is a write with consequences a reader should not be able to cause.
 */

export const RVM_PERMISSIONS = {
  settingsRead: 'rvm.settings.read',
  settingsManage: 'rvm.settings.manage',

  dimensionRead: 'rvm.dimension.read',
  dimensionManage: 'rvm.dimension.manage',

  forecastRead: 'rvm.forecast.read',
  forecastManage: 'rvm.forecast.manage',
  forecastApprove: 'rvm.forecast.approve',
  forecastPublish: 'rvm.forecast.publish',

  snapshotRead: 'rvm.snapshot.read',
  snapshotTake: 'rvm.snapshot.take',

  paceRead: 'rvm.pace.read',
  varianceExplain: 'rvm.variance.explain',

  accuracyRead: 'rvm.accuracy.read',
  accuracyRun: 'rvm.accuracy.run',

  hurdleRead: 'rvm.hurdle.read',
  hurdleManage: 'rvm.hurdle.manage',

  displacementRead: 'rvm.displacement.read',
  displacementEvaluate: 'rvm.displacement.evaluate',
  displacementDecide: 'rvm.displacement.decide',

  competitorRead: 'rvm.competitor.read',
  competitorManage: 'rvm.competitor.manage',
} as const

export type RvmPermission = (typeof RVM_PERMISSIONS)[keyof typeof RVM_PERMISSIONS]

/**
 * Naming a property, a room type or a channel is a PMS read, not an RVM one.
 *
 * Every RVM endpoint takes a `propertyId` and every on-the-books line carries a
 * `roomTypeId`, but `internal/rvm` publishes no lookup of its own — it reads the
 * PMS tables by SQL and never re-exposes them (§9.1). So the screens ask
 * `internal/pms` for the names, and a Revenue Manager does **not** hold
 * `pms.property.read` (`internal/core/rbac/roles.go:2720`).
 *
 * That is a real gap and it is handled rather than hidden: without the
 * permission the pickers fall back to entering an id and every label renders as
 * "Room type #12", with a line saying which permission would put a name there.
 * An empty dropdown would look like a property list with nothing in it.
 */
export const PMS_LOOKUP_PERMISSIONS = {
  propertyRead: 'pms.property.read',
  channelRead: 'pms.channel.read',
} as const
